/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useMemo, useState } from "react";
import { Box, Dialog, DialogContent, FormControl } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ConfirmationDialog } from "../confirmation_dialog";
import { LoadingSpinner } from "../loading_spinner";
import { ModalCancelButton } from "../modal_cancel_button";
import { ModuleAccessSelector } from "../module_access_selector";
import { SelectedMenuItem } from "../selected_menu_item";
import { StyledMenuItem } from "../styled_menu_item";
import { StyledSwitch } from "../total_sales_card/styles";
import { usePermissionModules } from "../../hooks/usePermissionModules";
import {
  CloseIconButton,
  ErrorText,
  FieldContainer,
  FieldLabel,
  FixedActionContainer,
  ProfileDialogPaperProps,
  RequiredMark,
  RoleChip,
  SaveButton,
  scrollableContent,
  SectionContainer,
  SectionHelperText,
  SectionTitle,
  SelectPlaceholder,
  selectMenuProps,
  StatusHelperText,
  StatusRow,
  StyledSelect,
  StyledTextField,
  TitleContainer,
  TitleText,
} from "./styles";

export const APPLICABLE_ROLE_OPTIONS = [
  { label: "Instance Admin", value: "admin" },
  { label: "Instance Viewer", value: "viewer" },
];

// Referenced by each input's `aria-describedby`, so a rejected field is announced rather than
// only being outlined in red.
const NAME_ERROR_ID = "permission-profile-name-error-text";
const ROLES_ERROR_ID = "permission-profile-roles-error-text";

export const NAME_MAX_LENGTH = 100;
export const DESCRIPTION_MAX_LENGTH = 255;

const NO_SERVER_ERRORS = {};

const EMPTY_FORM = {
  name: "",
  description: "",
  applicable_roles: [],
  active: true,
  module_keys: [],
};

/**
 * @description Maps a profile onto the form shape. Copies the arrays rather than sharing them,
 * so editing a profile never mutates the row the list is still rendering.
 * @param profile The profile being edited, or null when creating.
 * @returns The initial form state.
 */
const buildForm = (profile) => {
  if (!profile) return EMPTY_FORM;

  return {
    name: profile.name || "",
    description: profile.description || "",
    applicable_roles: [...(profile.applicable_roles || [])],
    active: profile.active !== false,
    module_keys: [...(profile.module_keys || [])],
  };
};

/**
 * @description Reads the first message the server reported for a field. Rails sends an array per
 * field, and only the first one is worth putting under the input.
 * @param serverErrors The `errors` object from a 422 response.
 * @param field The field name to read.
 * @returns The message, or an empty string.
 */
export const readServerError = (serverErrors, field) => {
  const value = serverErrors?.[field];

  if (Array.isArray(value)) return value.find((item) => typeof item === "string" && item) || "";

  return typeof value === "string" ? value : "";
};

/**
 * Create / edit form for a permission profile.
 *
 * Presentational: the caller owns the mutation and passes the outcome back in through
 * `isSubmitting` and `serverErrors`. It is mounted fresh per open (the page renders it only while
 * open), so the form always starts from the profile it was given and never carries state over
 * from the previous one.
 *
 * @param {Object}   profile       the profile being edited, or null to create
 * @param {Function} onSubmit      called with the payload once validation and confirmations pass
 * @param {Function} onClose       called to dismiss the modal
 * @param {Boolean}  isSubmitting  a save is in flight
 * @param {Object}   serverErrors  `errors` from the last 422, keyed by field
 * @param {Boolean}  readOnly      view-only mode, for admins who may read but not write
 */
export const PermissionProfileModal = ({
  open = true,
  profile = null,
  onSubmit,
  onClose,
  isSubmitting = false,
  serverErrors = NO_SERVER_ERRORS,
  readOnly = false,
}) => {
  const isEditMode = Boolean(profile?.id);
  const [form, setForm] = useState(() => buildForm(profile));
  const [errors, setErrors] = useState({});
  const [confirmations, setConfirmations] = useState([]);
  const [editedSinceResponse, setEditedSinceResponse] = useState({});
  const [reportedErrors, setReportedErrors] = useState(serverErrors);

  if (serverErrors !== reportedErrors) {
    setReportedErrors(serverErrors);
    setEditedSinceResponse({});
  }

  const { groups, moduleKeySet, selectableKeySet, isLoading, isError, refetch } =
    usePermissionModules({ enabled: open });

  const markEdited = useCallback((field) => {
    setErrors((current) => (current[field] ? { ...current, [field]: "" } : current));
    setEditedSinceResponse((current) => (current[field] ? current : { ...current, [field]: true }));
  }, []);

  const updateField = useCallback(
    (field, value) => {
      setForm((current) => ({ ...current, [field]: value }));
      markEdited(field);
    },
    [markEdited]
  );

  const handleRolesChange = useCallback(
    (event) => {
      const { value } = event.target;
      updateField("applicable_roles", typeof value === "string" ? value.split(",") : value);
    },
    [updateField]
  );

  const handleRoleDelete = useCallback(
    (role) => {
      setForm((current) => ({
        ...current,
        applicable_roles: current.applicable_roles.filter((item) => item !== role),
      }));
      markEdited("applicable_roles");
    },
    [markEdited]
  );

  const catalogueReady = !isLoading && !isError && moduleKeySet.size > 0;

  const cleanedModuleKeys = useMemo(
    () => (catalogueReady ? form.module_keys.filter((key) => selectableKeySet.has(key)) : form.module_keys),
    [form.module_keys, catalogueReady, selectableKeySet]
  );

  const payload = useMemo(() => {
    const base = {
      name: form.name.trim(),
      description: form.description.trim(),
      active: form.active,
      applicable_roles: form.applicable_roles,
    };

    // `ModuleAccessSelector` never emits a stale or inapplicable key once it is interacted with
    // (see its own `usableSelection`), so filtering here can only ever drop something when the
    // author never touched Module Access at all — a profile that legitimately still holds a key
    // since retired from the registry, being saved for an unrelated reason. Omitting the field in
    // that one case, rather than resubmitting the filtered copy, is what keeps that save from
    // silently stripping it: the backend already treats an omitted module_keys as "leave it as
    // is". A touched selection always submits cleanly either way, since there is nothing left for
    // the filter to catch.
    //
    // The same omission applies whenever the catalogue isn't ready (still loading, failed to
    // load, or empty): there is nothing to validate the stored selection against, so submitting
    // it verbatim can only ever resend possibly-retired keys the author never touched. Omitting
    // leaves them exactly as they are on the backend, same as the loaded-but-untouched case.
    if (!catalogueReady || cleanedModuleKeys.length !== form.module_keys.length) return base;

    return { ...base, module_keys: cleanedModuleKeys };
  }, [form, catalogueReady, cleanedModuleKeys]);

  /**
   * @description Consequences the author should see spelled out before the save goes through.
   * Both are legitimate saves, so they confirm rather than block.
   * @returns The messages to confirm, empty when the save needs no confirmation.
   */
  const pendingConfirmations = useMemo(() => {
    const messages = [];

    if (isEditMode && profile?.active && !form.active) {
      messages.push("This profile will be deactivated, so it can no longer be picked for a user. Users already on it keep the access they have today.");
    }

    // Reads the cleaned selection, not the raw form: a selection made entirely of keys the grid
    // cannot submit saves as an empty grant, and that is exactly when this warning matters most.
    if (cleanedModuleKeys.length === 0) {
      messages.push("This profile grants no modules, so a user assigned it will not be able to open any part of the Admin App.");
    }

    return messages;
  }, [isEditMode, profile?.active, form.active, cleanedModuleKeys.length]);

  /**
   * @description Mirrors the server's own rules so an obvious mistake never costs a round trip.
   * @returns Field errors, empty when the form is submittable.
   */
  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) nextErrors.name = "Profile name is required";
    if (form.applicable_roles.length === 0) {
      nextErrors.applicable_roles = "Select at least one applicable role";
    }

    return nextErrors;
  };

  const handleSubmit = () => {
    const nextErrors = validate();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});

    if (pendingConfirmations.length > 0) {
      setConfirmations(pendingConfirmations);
      return;
    }

    onSubmit?.(payload);
  };

  const handleConfirmSubmit = () => {
    setConfirmations([]);
    onSubmit?.(payload);
  };

  const serverError = (field) => (editedSinceResponse[field] ? "" : readServerError(serverErrors, field));

  const nameError = errors.name || serverError("name");
  const rolesError = errors.applicable_roles || serverError("applicable_roles");
  const modulesError = serverError("module_keys");
  const baseError = serverError("base");

  const title = readOnly ? "View Permission Profile" : isEditMode ? "Edit Permission Profile" : "Create Permission Profile";

  return (
    <React.Fragment>
      <Dialog
        data-testid="permission-profile-modal"
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={ProfileDialogPaperProps}
      >
        <TitleContainer>
          <TitleText data-testid="permission-profile-modal-title">{title}</TitleText>
          <CloseIconButton data-testid="permission-profile-modal-close-btn" onClick={onClose} disableRipple>
            <ClearIcon fontSize="small" />
          </CloseIconButton>
        </TitleContainer>

        <DialogContent sx={scrollableContent}>
          <SectionContainer>
            <FieldContainer>
              <FieldLabel htmlFor="permission-profile-name">
                Profile Name{!readOnly && <RequiredMark>*</RequiredMark>}
              </FieldLabel>
              <StyledTextField
                id="permission-profile-name"
                inputProps={{
                  "data-testid": "permission-profile-name-input",
                  maxLength: NAME_MAX_LENGTH,
                  "aria-describedby": nameError ? NAME_ERROR_ID : undefined,
                }}
                fullWidth
                required={!readOnly}
                size="small"
                placeholder="e.g. Partner Program Access"
                value={form.name}
                disabled={readOnly}
                onChange={(event) => updateField("name", event.target.value)}
                error={!!nameError}
                autoComplete="off"
              />
              {!!nameError && (
                <ErrorText id={NAME_ERROR_ID} role="alert" data-testid="permission-profile-name-error">
                  {nameError}
                </ErrorText>
              )}
            </FieldContainer>

            <FieldContainer>
              <FieldLabel htmlFor="permission-profile-description">Description</FieldLabel>
              <StyledTextField
                id="permission-profile-description"
                inputProps={{
                  "data-testid": "permission-profile-description-input",
                  maxLength: DESCRIPTION_MAX_LENGTH,
                }}
                fullWidth
                multiline
                minRows={2}
                size="small"
                placeholder="What this profile is for"
                value={form.description}
                disabled={readOnly}
                onChange={(event) => updateField("description", event.target.value)}
                autoComplete="off"
              />
            </FieldContainer>

            <FieldContainer>
              <FieldLabel id="permission-profile-roles-label">
                Applicable Roles{!readOnly && <RequiredMark>*</RequiredMark>}
              </FieldLabel>
              <FormControl fullWidth size="small" sx={{ marginTop: "4px" }}>
                <StyledSelect
                  labelId="permission-profile-roles-label"
                  data-testid="permission-profile-roles-select"
                  multiple
                  displayEmpty
                  size="small"
                  disabled={readOnly}
                  required={!readOnly}
                  value={form.applicable_roles}
                  onChange={handleRolesChange}
                  error={!!rolesError}
                  aria-describedby={rolesError ? ROLES_ERROR_ID : undefined}
                  MenuProps={selectMenuProps}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <SelectPlaceholder>Select the roles this profile applies to...</SelectPlaceholder>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {selected.map((role) => (
                          <RoleChip
                            key={role}
                            label={APPLICABLE_ROLE_OPTIONS.find((option) => option.value === role)?.label || role}
                            {...(readOnly
                              ? {}
                              : {
                                  onDelete: () => handleRoleDelete(role),
                                  onMouseDown: (event) => event.stopPropagation(),
                                })}
                          />
                        ))}
                      </Box>
                    )
                  }
                >
                  {APPLICABLE_ROLE_OPTIONS.map((option) => (
                    <StyledMenuItem key={option.value} value={option.value}>
                      {form.applicable_roles.includes(option.value) ? (
                        <SelectedMenuItem data={option.label} />
                      ) : (
                        option.label
                      )}
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
              {!!rolesError && (
                <ErrorText id={ROLES_ERROR_ID} role="alert" data-testid="permission-profile-roles-error">
                  {rolesError}
                </ErrorText>
              )}
            </FieldContainer>

            <StatusRow>
              <Box>
                <FieldLabel as="div">Status</FieldLabel>
                <StatusHelperText data-testid="permission-profile-status-helper">
                  {form.active
                    ? "Active - this profile can be picked when setting up a user."
                    : "Inactive - this profile cannot be picked for new users."}
                </StatusHelperText>
              </Box>
              <StyledSwitch
                data-testid="permission-profile-status-switch"
                inputProps={{ "aria-label": "Status" }}
                checked={form.active}
                disabled={readOnly}
                onChange={(event) => updateField("active", event.target.checked)}
              />
            </StatusRow>
          </SectionContainer>

          <SectionContainer>
            <SectionTitle>Module Access</SectionTitle>
            <SectionHelperText>
              Choose which parts of the Admin App this profile grants. A user's role still decides
              what they can do inside those modules.
            </SectionHelperText>
            {isEditMode && (
              <SectionHelperText data-testid="permission-profile-modules-existing-users-note">
                Changing this does not move users already assigned this profile — only new
                assignments pick up the new selection.
              </SectionHelperText>
            )}

            <ModuleAccessSelector
              groups={groups}
              selectedKeys={form.module_keys}
              moduleKeySet={moduleKeySet}
              selectableKeySet={selectableKeySet}
              onChange={(nextKeys) => updateField("module_keys", nextKeys)}
              isLoading={isLoading}
              isError={isError}
              onRetry={refetch}
              disabled={readOnly}
              testIdPrefix="permission-profile-modules"
              emptySelectionMessage="No modules selected - a user assigned this profile will not be able to open any part of the Admin App."
            />

            {!!modulesError && (
              <ErrorText role="alert" data-testid="permission-profile-modules-error">
                {modulesError}
              </ErrorText>
            )}
            {!!baseError && (
              <ErrorText role="alert" data-testid="permission-profile-base-error">
                {baseError}
              </ErrorText>
            )}
          </SectionContainer>
        </DialogContent>

        <FixedActionContainer>
          <ModalCancelButton
            data-testid="permission-profile-modal-cancel-btn"
            disableRipple
            onClick={onClose}
            disabled={isSubmitting}
          >
            {readOnly ? "Close" : "Cancel"}
          </ModalCancelButton>

          {!readOnly && (
            <SaveButton
              data-testid="permission-profile-modal-save-btn"
              disableRipple
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoadingSpinner isLoading={true} /> : isEditMode ? "Update Profile" : "Create Profile"}
            </SaveButton>
          )}
        </FixedActionContainer>
      </Dialog>

      <ConfirmationDialog
        open={confirmations.length > 0}
        onClose={() => setConfirmations([])}
        Icon={ErrorOutlineIcon}
        dialogTitle="Please confirm before saving"
        dialogBody={
          <Box component="span" data-testid="permission-profile-confirm-body">
            {confirmations.map((message) => (
              <Box component="span" key={message} sx={{ display: "block", marginBottom: "6px" }}>
                {message}
              </Box>
            ))}
          </Box>
        }
        confirmationText="Yes, save this profile"
        handleConfirm={handleConfirmSubmit}
        isLoading={isSubmitting}
        dataTestId="permission-profile-confirm-dialog"
      />
    </React.Fragment>
  );
};

export default PermissionProfileModal;
